import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { navLinks } from "./navLinks";
import { useScrolled } from "@/hooks/useScrolled";

export default function SiteHeader() {
  const scrolled = useScrolled(20);
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/topics?q=${encodeURIComponent(value)}` : "/topics");
    setOpen(false);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled
          ? "border-background-200 bg-background-50/95 backdrop-blur-md"
          : "border-transparent bg-background-50"
      }`}
    >
      <div className="mx-auto flex h-16 w-full items-center gap-4 px-4 md:h-[68px] md:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50 dark:text-foreground-950">
            <i className="ri-mic-2-fill text-lg"></i>
          </span>
          <span className="font-heading text-lg font-black tracking-tight text-foreground-950 whitespace-nowrap">
            高校吃瓜网
          </span>
        </Link>

        <nav aria-label="主导航" className="ml-2 hidden items-center gap-1 md:flex">
          {navLinks.map((link) => {
            const active =
              link.to === "/"
                ? location.pathname === "/"
                : location.pathname.startsWith(link.to);
            return (
              <Link
                key={link.to}
                to={link.to}
                className={`rounded-md px-3.5 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                  active
                    ? "bg-primary-100 text-primary-700"
                    : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                }`}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>

        <div className="ml-auto flex items-center gap-3">
          <form onSubmit={handleSearch} className="hidden items-center lg:flex">
            <div className="flex h-10 items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4">
              <i className="ri-search-line text-base text-foreground-500"></i>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索学校或话题…"
                aria-label="搜索学校或话题"
                className="w-40 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none xl:w-52"
              />
            </div>
          </form>

          <Link
            to="/topics"
            className="hidden items-center gap-1.5 rounded-md bg-foreground-950 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-foreground-800 whitespace-nowrap md:flex"
          >
            <i className="ri-add-line text-base"></i>
            去讨论区
          </Link>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "关闭菜单" : "打开菜单"}
            aria-expanded={open}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-900 md:hidden"
          >
            <i className={`text-xl ${open ? "ri-close-line" : "ri-menu-3-line"}`}></i>
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-3 md:hidden">
          <form onSubmit={handleSearch} className="mb-3 flex h-10 items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4">
            <i className="ri-search-line text-base text-foreground-500"></i>
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索学校或话题…"
              aria-label="移动端搜索学校或话题"
              className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
            />
          </form>
          <nav aria-label="移动端导航" className="flex flex-col">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100"
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/topics"
              onClick={() => setOpen(false)}
              className="mt-2 rounded-md bg-foreground-950 px-3 py-3 text-center text-sm font-medium text-background-50"
            >
              去讨论区
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}